// Anonymous analytics identity. Raw wallet addresses must never leave the
// browser (PII rule) — only a truncated SHA-256 digest is used.

const STORAGE_KEY = "__nexus_dapp_ui_uid";

let memoryAnonId: string | null = null;

function randomUuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join("");
}

/** Stable per-browser id, e.g. `anon_3f8a…`. Falls back to memory when localStorage throws. */
export function getDappUiAnonId(): string {
  if (memoryAnonId) return memoryAnonId;
  let stored: string | null = null;
  try {
    stored = window.localStorage.getItem(STORAGE_KEY);
  } catch {
    // private mode / storage disabled
  }
  if (!stored || !stored.startsWith("anon_")) {
    stored = `anon_${randomUuid()}`;
    try {
      window.localStorage.setItem(STORAGE_KEY, stored);
    } catch {
      // keep in memory only
    }
  }
  memoryAnonId = stored;
  return stored;
}

/** `wallet_<first 16 hex chars of sha256(lowercased address)>`; null when hashing is unavailable. */
export async function hashWalletAddress(address: string): Promise<string | null> {
  if (typeof crypto === "undefined" || !crypto.subtle) return null;
  try {
    const bytes = new TextEncoder().encode(address.trim().toLowerCase());
    const digest = await crypto.subtle.digest("SHA-256", bytes);
    const hex = Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
    return `wallet_${hex.slice(0, 16)}`;
  } catch {
    return null;
  }
}
